import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { DocumentNode, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { AI_INTAKE_BATCH_STATUS } from './gql';
import './intake-dropzone.scss';

// ---------------------------------------------------------------------------
// Props contract
// ---------------------------------------------------------------------------
/**
 * Minimal per-file shape every batch-status query must return. Callers that
 * need richer rows (e.g. Bulk Add's candidate / aiScore fields) get the raw
 * server rows back through `onComplete`.
 */
export interface IntakeFileStatusRow {
  fileId: string;
  fileName: string;
  status: string;
  reason?: string | null;
  [key: string]: unknown;
}

export interface IntakeDropzoneProps {
  /** Only sent by the default (AI Recruiter intake) upload; optional elsewhere. */
  companyId?: string;
  jobOrderId: string;
  onClose: () => void;
  /** Called once with the batchId when every file reaches a terminal status. */
  onUploaded?: (batchId: string) => void;
  /** REST path appended to REACT_APP_API_BASE_URL. Default: AI Recruiter intake. */
  uploadPath?: string;
  /**
   * Extra multipart body fields sent next to `files`.
   * Default: { companyId, jobOrderId } (AI Recruiter intake contract).
   */
  extraFields?: Record<string, string>;
  /** Batch-status query taking `$batchId: ID!`. Default: AI_INTAKE_BATCH_STATUS. */
  statusQuery?: DocumentNode;
  /** Picks the file list out of the status query result. Default: data.aiIntakeBatchStatus. */
  selectStatusFiles?: (data: any) => IntakeFileStatusRow[] | null | undefined;
  /** Accepted file extensions (lowercase, with dot). Default: .pdf .doc .docx. */
  acceptedExtensions?: string[];
  /** Accepted MIME types. A file passes if its extension OR its MIME type matches. */
  acceptedMimes?: string[];
  /** Human label for the accepted types, used in the hint and the reject toast. */
  acceptedLabel?: string;
  /** Hint suffix after the size limits. Default mentions company & job order context. */
  hintSuffix?: string;
  /** Message shown once the batch completes. */
  completionText?: string;
  /** Whether to render the built-in Close button. Default true. */
  showCloseButton?: boolean;
  /** Fired once with the final (all-terminal) server rows. */
  onComplete?: (files: IntakeFileStatusRow[]) => void;
  /** Fired when an upload / processing run starts or finishes (true while in flight). */
  onBusyChange?: (busy: boolean) => void;
  /** Overrides / additions to the reason-code → label map. */
  reasonLabels?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FileStatus = 'pending' | 'processing' | 'done' | 'failed';

interface IntakeFile {
  fileId: string;
  fileName: string;
  status: FileStatus;
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// Reason-code → friendly label map
// Safe codes sent by the server (see §6b security#3 of the plan).
// ---------------------------------------------------------------------------

export const REASON_LABELS: Record<string, string> = {
  UNSUPPORTED_FILE: 'File type not supported (PDF, DOC, or DOCX required).',
  PARSE_FAILED: 'Could not read the resume — the file may be corrupt or image-only.',
  UPLOAD_FAILED: 'Upload to storage failed. Please try again.',
  DUPLICATE: 'This resume is already in the system for this job order.',
};

const reasonLabel = (
  code: string | null | undefined,
  labels: Record<string, string> = REASON_LABELS
): string => {
  if (!code) return 'An unexpected error occurred. Please try again.';
  return labels[code] ?? 'An unexpected error occurred. Please try again.';
};

// ---------------------------------------------------------------------------
// Terminal-status check
// ---------------------------------------------------------------------------

const TERMINAL: Set<string> = new Set(['done', 'failed']);

const allTerminal = (files: IntakeFile[]): boolean =>
  files.length > 0 && files.every((f) => TERMINAL.has(f.status));

// ---------------------------------------------------------------------------
// Accepted MIME types / extensions
// ---------------------------------------------------------------------------

// Hard limits — must match the backend FilesInterceptor cap.
const MAX_FILES = 50;
const MAX_FILE_SIZE_MB = 30;

const DEFAULT_ACCEPTED_EXTS = ['.pdf', '.doc', '.docx'];
const DEFAULT_ACCEPTED_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const DEFAULT_ACCEPTED_LABEL = 'PDF, DOC, DOCX';
const DEFAULT_HINT_SUFFIX = 'company & job order set by context';
const DEFAULT_COMPLETION_TEXT =
  'Batch complete. New applicants will appear in the table above.';
const DEFAULT_UPLOAD_PATH = '/ai-recruiter/intake/upload';
const defaultSelectStatusFiles = (data: any) => data?.aiIntakeBatchStatus;

const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
};

// ---------------------------------------------------------------------------
// Per-file status icon
// ---------------------------------------------------------------------------

const FileStatusIcon: FC<{ status: FileStatus }> = ({ status }) => {
  if (status === 'pending') {
    // Hollow ring using a simple SVG
    return (
      <span
        className="ai-console__intake-icon ai-console__intake-icon--pending"
        aria-label="Pending"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="7" stroke="#6c757d" strokeWidth="2" />
        </svg>
      </span>
    );
  }
  if (status === 'processing') {
    return (
      <Spinner
        size="sm"
        className="ai-console__intake-icon ai-console__intake-icon--processing"
        aria-label="Processing"
      />
    );
  }
  if (status === 'done') {
    return (
      <span
        className="ai-console__intake-icon ai-console__intake-icon--done"
        aria-label="Done"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="8" fill="#28a745" />
          <path d="M4.5 8l2.5 2.5 4.5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  // failed
  return (
    <span
      className="ai-console__intake-icon ai-console__intake-icon--failed"
      aria-label="Failed"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill="#dc3545" />
        <path d="M5 5l6 6M11 5l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const IntakeDropzone: FC<IntakeDropzoneProps> = ({
  companyId,
  jobOrderId,
  onClose,
  onUploaded,
  uploadPath = DEFAULT_UPLOAD_PATH,
  extraFields,
  statusQuery = AI_INTAKE_BATCH_STATUS,
  selectStatusFiles = defaultSelectStatusFiles,
  acceptedExtensions = DEFAULT_ACCEPTED_EXTS,
  acceptedMimes = DEFAULT_ACCEPTED_MIMES,
  acceptedLabel = DEFAULT_ACCEPTED_LABEL,
  hintSuffix = DEFAULT_HINT_SUFFIX,
  completionText = DEFAULT_COMPLETION_TEXT,
  showCloseButton = true,
  onComplete,
  onBusyChange,
  reasonLabels = REASON_LABELS,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [files, setFiles] = useState<IntakeFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track whether onUploaded has been called so we only call it once.
  const uploadedCalledRef = useRef(false);

  // Callbacks via refs so parents can pass inline lambdas without retriggering effects.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  const selectStatusFilesRef = useRef(selectStatusFiles);
  selectStatusFilesRef.current = selectStatusFiles;

  const acceptedExtSet = useMemo(
    () => new Set(acceptedExtensions.map((e) => e.toLowerCase())),
    [acceptedExtensions]
  );
  const acceptedMimeSet = useMemo(() => new Set(acceptedMimes), [acceptedMimes]);
  const acceptAttr = acceptedExtensions.join(',');

  // Extension OR MIME: Word files frequently arrive with an empty `type`.
  const filterAccepted = useCallback(
    (list: FileList): File[] =>
      Array.from(list).filter(
        (f) => acceptedMimeSet.has(f.type) || acceptedExtSet.has(fileExtension(f.name))
      ),
    [acceptedExtSet, acceptedMimeSet]
  );

  // Default multipart fields preserve the AI Recruiter intake contract.
  const bodyFields = useMemo<Record<string, string>>(
    () => extraFields ?? { companyId: companyId ?? '', jobOrderId },
    [extraFields, companyId, jobOrderId]
  );

  // ---- Batch-status poll ----
  const { data: pollData, stopPolling } = useQuery(statusQuery, {
    variables: { batchId: batchId ?? '' },
    pollInterval: 2000,
    skip: !batchId,
    fetchPolicy: 'network-only',
  });

  // Merge server status into local file list and stop polling when done.
  useEffect(() => {
    const rows = selectStatusFilesRef.current(pollData);
    if (!rows) return;

    const serverFiles: IntakeFile[] = rows.map(
      (f: IntakeFileStatusRow) => ({
        fileId: f.fileId,
        fileName: f.fileName,
        status: f.status as FileStatus,
        reason: f.reason,
      })
    );

    setFiles(serverFiles);

    if (allTerminal(serverFiles)) {
      stopPolling();
      if (!uploadedCalledRef.current && batchId) {
        uploadedCalledRef.current = true;
        onUploaded?.(batchId);
        onCompleteRef.current?.(rows);
        onBusyChangeRef.current?.(false);
      }
    }
  }, [pollData, batchId, stopPolling, onUploaded]);

  // ---- Upload handler ----
  const handleUpload = useCallback(
    async (selected: File[]) => {
      if (selected.length === 0) return;
      if (selected.length > MAX_FILES) {
        toast.error(
          `You can upload up to ${MAX_FILES} resumes at a time. Please select ${MAX_FILES} or fewer.`
        );
        return;
      }
      const oversized = selected.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
      if (oversized.length > 0) {
        toast.error(
          `${oversized.length} file${oversized.length !== 1 ? 's exceed' : ' exceeds'} the ${MAX_FILE_SIZE_MB} MB limit: ${oversized
            .slice(0, 3)
            .map((f) => f.name)
            .join(', ')}${oversized.length > 3 ? '…' : ''}`
        );
        return;
      }

      setUploading(true);
      onBusyChangeRef.current?.(true);
      // Show optimistic pending list immediately.
      setFiles(
        selected.map((f, idx) => ({
          fileId: `local-${idx}`,
          fileName: f.name,
          status: 'pending',
        }))
      );

      try {
        const formData = new FormData();
        selected.forEach((f) => formData.append('files', f));
        Object.entries(bodyFields).forEach(([key, value]) => formData.append(key, value));

        const apiBaseURL =
          process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

        const response = await fetch(`${apiBaseURL}${uploadPath}`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: 'Bearer ' + localStorage.token,
          },
        });

        if (!response.ok) {
          const isAuthError = response.status === 401 || response.status === 403;
          throw new Error(
            isAuthError
              ? 'You do not have permission to upload resumes.'
              : `Upload failed (${response.status}).`
          );
        }

        const result: {
          batchId: string;
          files: Array<{ fileId: string; fileName: string; status: string }>;
        } = await response.json();

        // Seed the file list from the server's response before polling starts.
        setFiles(
          result.files.map((f) => ({
            fileId: f.fileId,
            fileName: f.fileName,
            status: f.status as FileStatus,
          }))
        );

        setBatchId(result.batchId);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Resume upload failed. Please try again.';
        toast.error(message);
        // Clear the optimistic list so the user can retry.
        setFiles([]);
        onBusyChangeRef.current?.(false);
      } finally {
        setUploading(false);
      }
    },
    [bodyFields, uploadPath]
  );

  // ---- Drag-and-drop handlers ----
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (uploading || batchId) return;
      const accepted = filterAccepted(e.dataTransfer.files);
      if (accepted.length === 0) {
        toast.error(`Only ${acceptedLabel} files are accepted.`);
        return;
      }
      handleUpload(accepted);
    },
    [uploading, batchId, handleUpload, filterAccepted, acceptedLabel]
  );

  // ---- File-picker change ----
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || uploading || batchId) return;
      const accepted = filterAccepted(e.target.files);
      if (accepted.length === 0) {
        toast.error(`Only ${acceptedLabel} files are accepted.`);
        return;
      }
      handleUpload(accepted);
      // Reset the input value so the same file can be re-selected after a failure.
      e.target.value = '';
    },
    [uploading, batchId, handleUpload, filterAccepted, acceptedLabel]
  );

  const isProcessing = batchId !== null && !allTerminal(files);
  const isDone = batchId !== null && allTerminal(files);
  const showDropzone = batchId === null;

  return (
    <div className="ai-console__intake-dropzone">
      {/* Dropzone / hint area — hidden once a batch is in flight */}
      {showDropzone && (
        <div
          className={`ai-console__intake-zone${isDragOver ? ' ai-console__intake-zone--over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Drop zone for resume files"
          onClick={() => !uploading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!uploading) fileInputRef.current?.click();
            }
          }}
        >
          <p className="ai-console__intake-hint">
            Drag &amp; drop resumes here &middot; {acceptedLabel} &middot; up to {MAX_FILES} files,{' '}
            {MAX_FILE_SIZE_MB} MB each{hintSuffix ? <> &middot; {hintSuffix}</> : null}
          </p>
          <Button
            color="secondary"
            size="sm"
            disabled={uploading}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            {uploading ? <Spinner size="sm" /> : 'Browse files'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Per-file status list */}
      {files.length > 0 && (
        <ul className="ai-console__intake-file-list">
          {files.map((f) => (
            <li key={f.fileId} className={`ai-console__intake-file-item ai-console__intake-file-item--${f.status}`}>
              <FileStatusIcon status={f.status} />
              <span className="ai-console__intake-file-name">{f.fileName}</span>
              {f.status === 'processing' && (
                <span className="ai-console__intake-file-sub text-muted">Processing…</span>
              )}
              {f.status === 'pending' && (
                <span className="ai-console__intake-file-sub text-muted">Waiting…</span>
              )}
              {f.status === 'failed' && (
                <span className="ai-console__intake-file-sub ai-console__intake-file-reason">
                  {reasonLabel(f.reason, reasonLabels)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Footer: loading indicator or done message */}
      {isProcessing && (
        <p className="ai-console__intake-progress text-muted">
          <Spinner size="sm" className="mr-1" /> Processing resumes…
        </p>
      )}
      {isDone && (
        <p className="ai-console__intake-done">{completionText}</p>
      )}

      {/* Close button (optional; hosts with their own footer hide it) */}
      {showCloseButton && (
        <Button
          color="secondary"
          size="sm"
          className="ai-console__intake-close mt-2"
          onClick={onClose}
        >
          Close
        </Button>
      )}
    </div>
  );
};

export default IntakeDropzone;
